import { create } from 'zustand';
import type { Product } from '../types/product';
import type { BulkUpdates } from '../types/bulk';
import { useAuthStore } from './authStore';
import { supabase } from '../utils/supabase';
import { saveProductImage, deleteProductImages } from '../utils/imageStorage';
import { mapProductFromDB, mapProductToDB } from '../utils/supabase/mappers';
import { useTemplateStore } from './templateStore';
import { useFolderStore } from './folderStore';
import { getUserCreditsData } from '../utils/supabase';
import { SUBSCRIPTION_PLANS } from '../utils/stripe/config';

interface ProductState {
  products: Product[];
  lastSerialNumber: number;
  isLoading: boolean;
  useDescriptionTemplate: boolean;
  loadProducts: () => Promise<void>;
  addProduct: (useTemplate?: boolean) => Promise<Product>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (productId: number) => Promise<void>;
  deleteMultipleProducts: (productIds: number[]) => Promise<void>;
  updateMultipleProducts: (updates: BulkUpdates, selectedIds: number[]) => Promise<void>;
  toggleProductSelection: (productId: number) => void;
  selectAllProducts: (selected: boolean) => void;
  clearProductSelections: () => void;
  setUseDescriptionTemplate: (use: boolean) => void;
}

export const useProductStore = create<ProductState>((set, get) => ({
  products: [],
  lastSerialNumber: 0,
  isLoading: false,
  useDescriptionTemplate: true,

  loadProducts: async () => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    set({ isLoading: true });
    try {
      // Test Supabase connection first
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase query error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      let maxSerial = 0;
      if (data && data.length > 0) {
        maxSerial = Math.max(...data.map(p => p.serial_number || 0));
      }

      const products = data.map(p => mapProductFromDB(p));

      set({ products, lastSerialNumber: maxSerial, isLoading: false });
    } catch (error) {
      console.error('Failed to load products - Full error details:', error);
      
      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('Network error: Cannot connect to Supabase. Please check:');
        console.error('1. Internet connection');
        console.error('2. Supabase URL and keys in .env file');
        console.error('3. Supabase project status');
        console.error('4. CORS settings in Supabase dashboard');
      }
      
      set({ isLoading: false });
      throw error;
    }
  },

  addProduct: async (useTemplate = false) => {
    const user = useAuthStore.getState().user;
    if (!user) throw new Error('User not authenticated');

    // ユーザーのクレジットデータを取得して製品上限をチェック
    const userCredits = await getUserCreditsData(user.id);
    const currentProductCount = userCredits.product_count || 0;
    const maxProductsLimit = userCredits.max_products || 30;
    
    // ユーザーのプランを取得
    const { data: memberData } = await supabase
      .from('users')
      .select('plan')
      .eq('id', user.id)
      .maybeSingle();
    
    const userPlan = memberData?.plan || 'unsubscribed';

    if (currentProductCount >= maxProductsLimit) {
      if (userPlan === 'enterprise' || maxProductsLimit >= 1000) {
        throw new Error('これ以上商品を登録できません。上限の1000個に達しています。');
      } else {
        // 次のプランを見つける
        // const currentPlan = SUBSCRIPTION_PLANS.find(p => p.id === userPlan);
        const nextPlan = SUBSCRIPTION_PLANS.find(p => p.productLimit > maxProductsLimit);
        
        if (nextPlan) {
          throw new Error(`製品登録数の上限（${maxProductsLimit}個）に達しました。${nextPlan.name}プラン（月額${nextPlan.price.toLocaleString()}円）にアップグレードすると、${nextPlan.productLimit}個まで登録できます。`);
        } else {
          throw new Error(`製品登録数の上限（${maxProductsLimit}個）に達しました。プランをアップグレードしてください。`);
        }
      }
    }

    const { lastSerialNumber } = get();
    const currentFolderId = useFolderStore.getState().currentFolderId;
    const newSerialNumber = lastSerialNumber + 1;
    const templateStore = useTemplateStore.getState();
    const activeTemplateId = templateStore.activeTemplateId;

    let baseProduct: Omit<Product, 'id'> = {
      serialNumber: newSerialNumber,
      title: '',
      category: '',
      categoryYahoo: '',
      startPrice: 1000,
      buyItNowPrice: undefined,
      quantity: 1,
      auctionDuration: 7,
      endTime: '22',
      description: '',
      descriptionTemplateId: null,
      condition: '',
      itemNumber: '',
      itemNumber2: '',
      itemNumberPrefix: false,
      itemNumberSuffix: false,
      itemNumberSerial: false,
      memo: '',
      memoPrefix: false,
      memoSuffix: false,
      memoSerial: false,
      autoExtension: false,
      autoExtensionRate: 5,
      autoExtensionCount: 1,
      autoExtensionByBid: true,
      allowHtmlInDescription: false,
      thumbnailImages:[],
      images: [],
      shippingUnspecified: false,
      shippingLater: false,
      shippingCOD: false,
      shippingResponsibility: 'buyer',
      shippingTime: '2-3',
      shippingLocation: '',
      nekoposu: false,
      nekoCompact: false,
      nekoEazy: false,
      nekoEazySize: '',
      nekoEazyWeight: '',
      yuPacketMini: false,
      yuPacket: false,
      yuPacketPlus: false,
      yuPack: false,
      yuPackSize: '',
      yuPackWeight: '',
      customShippingMethods: [],
      listingType: 'auction',
      ratingRestriction: true,
      badRatingRestriction: true,
      bidderAuthentication: false,
      returnsAccepted: false,
      isFeatured: false,
      featuredMinPrice: 0,
      featuredMaxPrice: 0,
      recommendedCollection: false,
      recommendedCollectionRate: 0,
      isSelected: false,
      folderId: currentFolderId
    };

    if (useTemplate && activeTemplateId) {
      const template = templateStore.getTemplateById(activeTemplateId);
      if (template) {
        baseProduct = {
          ...baseProduct,
          ...template.fields,
          customShippingMethods: [...template.fields.customShippingMethods]
        };
      }
    }

    try {
      const supabaseProduct = {
        ...mapProductToDB(baseProduct as Product),
        user_id: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      delete supabaseProduct.id; // Remove id for insert

      const { data, error } = await supabase
        .from('products')
        .insert(supabaseProduct)
        .select()
        .single();

      if (error) throw error;

      console.log('■■Supabaseが了承した■■: 商品データがSupabaseに挿入されました');

      // 製品作成成功後、product_countをインクリメント
      // 直接データベースでインクリメントする（アトミック操作）
      const { error: incrementError } = await supabase.rpc('increment_product_count', {
        p_user_id: user.id
      });
      if (incrementError) throw incrementError;

      const newProduct: Product = mapProductFromDB(data);

      set(state => ({
        products: [newProduct, ...state.products],
        lastSerialNumber: newSerialNumber
      }));

      return newProduct;
    } catch (error) {
      console.error('Failed to create product:', error);
      throw error;
    }
  },

  updateProduct: async (product: Product) => {
    try {
      // Get current Supabase quality setting from localStorage
      const getSupabaseQuality = () => {
        try {
          const saved = localStorage.getItem('imageSettings_supabaseQuality');
          return saved !== null ? JSON.parse(saved) / 100 : 0.20; // Convert percentage to decimal, default 20%
        } catch {
          return 0.20; // Default to 20%
        }
      };
      
      const supabaseQuality = getSupabaseQuality();
      
      // Handle image uploads if there are base64 images
      const processedImages = await Promise.all(
        product.images
          .filter(Boolean)
          .map(async (image) => {
          if (image.startsWith('http')) {
            console.log('🔗 既存URL: オリジナル画像はすでにアップロード済み');
            return image;
          }
          console.log('💾 新規アップロード: オリジナル画像をSupabaseにアップロード中...');
          return await saveProductImage(product.id, image, false, supabaseQuality);
        })
      );
      
      // Handle thumbnail image uploads
      const thumbnailQuality = Math.max(0.5, supabaseQuality - 0.15); // サムネイルはメイン画像より少し低い画質
      const processedThumbnailImages = await Promise.all(
        product.thumbnailImages
          .filter(Boolean)
          .map(async (image) => {
          // If the image is already a URL, return it as is
          if (image.startsWith('http')) {
            console.log('🔗 既存URL: サムネイル画像はすでにアップロード済み');
            return image;
          }
          console.log('💾 新規アップロード: サムネイル画像をSupabaseにアップロード中...');
          return await saveProductImage(product.id, image, true, thumbnailQuality);
        })
      );

      console.log('📦 アップロード完了: 全ての画像処理が完了');
      
      // Update product with processed images
      const updatedProduct = {
        ...product,
        images: processedImages,
        thumbnailImages: processedThumbnailImages
      };

      console.log('🗄️ データベース更新: 商品データをSupabaseに保存中...');
      // Map to DB format and update in Supabase
      const supabaseProduct = mapProductToDB(updatedProduct);
      const { error } = await supabase
        .from('products')
        .update(supabaseProduct)
        .eq('id', product.id);

      if (error) throw error;
      console.log('✅ 商品更新完了: データベースへの保存が完了しました');

      // Update local state
      set(state => ({
        products: state.products.map(p =>
          p.id === product.id ? updatedProduct : p
        )
      }));
      
      console.log('🎉 全工程完了: 商品のサムネイル保存処理が正常に完了');
    } catch (error) {
      console.error('❌ 商品更新失敗: 処理中にエラーが発生しました', error);
      throw error;
    }
  },

  deleteProduct: async (productId: number) => {
    try {
      // First delete all images from storage
      const user = useAuthStore.getState().user;
      if (!user) throw new Error('User not authenticated');
      
      await deleteProductImages(productId);

      // Then delete the product from the database
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      // 製品削除成功後、product_countをデクリメント（アトミック操作）
      const { error: decrementError } = await supabase.rpc('decrement_product_count', {
        p_user_id: user.id
      });
      if (decrementError) {
        console.error('Failed to decrement product count:', decrementError);
        // Continue anyway since the product was deleted
      }

      // Update local state
      set(state => ({
        products: state.products.filter(p => p.id !== productId)
      }));
    } catch (error) {
      console.error('Failed to delete product:', error);
      throw error;
    }
  },

  deleteMultipleProducts: async (productIds: number[]) => {
    try {
      // Delete images for all products
      const user = useAuthStore.getState().user;
      if (!user) throw new Error('User not authenticated');
      
      await Promise.all(productIds.map(id => deleteProductImages(id)));

      // Delete products from database
      const { error } = await supabase
        .from('products')
        .delete()
        .in('id', productIds);

      if (error) throw error;

      // 製品削除成功後、product_countをデクリメント
      // 複数の製品を削除した場合は、その数だけデクリメント（アトミック操作）
      const { error: decrementError } = await supabase.rpc('decrement_product_count_by', {
        p_user_id: user.id,
        p_count: productIds.length
      });
      if (decrementError) {
        console.error('Failed to decrement product count:', decrementError);
        // Continue anyway since the products were deleted
      }

      // Update local state
      set(state => ({
        products: state.products.filter(p => !productIds.includes(p.id))
      }));
    } catch (error) {
      console.error('Failed to delete multiple products:', error);
      throw error;
    }
  },

  updateMultipleProducts: async (updates: BulkUpdates, selectedIds: number[]) => {
    const { products } = get();
    const updatedProducts = products.map(product => {
      if (!selectedIds.includes(product.id)) return product;
      const updated = { ...product };
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) (updated as any)[key] = value;
      });
      return updated;
    });

    try {
      // Update each product in Supabase
      await Promise.all(
        updatedProducts
          .filter(p => selectedIds.includes(p.id))
          .map(async (product) => {
            const supabaseProduct = mapProductToDB(product);
            const { error } = await supabase
              .from('products')
              .update(supabaseProduct)
              .eq('id', product.id);
            if (error) throw error;
          })
      );

      // Update local state
      set({ products: updatedProducts });
    } catch (error) {
      console.error('Failed to update multiple products:', error);
      throw error;
    }
  },

  toggleProductSelection: (productId: number) => {
    set(state => ({
      products: state.products.map(product =>
        product.id === productId
          ? { ...product, isSelected: !product.isSelected }
          : product
      )
    }));
  },

  selectAllProducts: (selected: boolean) => {
    const currentFolderId = useFolderStore.getState().currentFolderId;

    set(state => ({
      products: state.products.map(product => ({
        ...product,
        isSelected:
          currentFolderId === null
            ? selected
            : product.folderId === currentFolderId
              ? selected
              : product.isSelected
      }))
    }));
  },

  clearProductSelections: () => {
    set(state => ({
      products: state.products.map(product => ({
        ...product,
        isSelected: false
      }))
    }));
  },

  setUseDescriptionTemplate: (use: boolean) => {
    set({ useDescriptionTemplate: use });
  }
}));